import { useState, useRef, useEffect } from "react";

const purchaseHistory = [
  { date: "2026-03-28", amount: 200, method: "微信支付", type: "充值", balance: 328.50, icon: "ri-wechat-pay-line", iconColor: "text-green-500" },
  { date: "2026-03-01", amount: 102.56, method: "银行代扣", type: "月度账单", balance: 128.50, icon: "ri-bank-line", iconColor: "text-gray-500" },
  { date: "2026-02-01", amount: 134.20, method: "支付宝", type: "月度账单", balance: 231.06, icon: "ri-alipay-line", iconColor: "text-sky-500" },
  { date: "2026-01-15", amount: 100, method: "微信支付", type: "充值", balance: 365.26, icon: "ri-wechat-pay-line", iconColor: "text-green-500" },
  { date: "2026-01-01", amount: 118.90, method: "银行代扣", type: "月度账单", balance: 265.26, icon: "ri-bank-line", iconColor: "text-gray-500" },
  { date: "2025-12-15", amount: 200, method: "微信支付", type: "充值", balance: 384.16, icon: "ri-wechat-pay-line", iconColor: "text-green-500" },
  { date: "2025-12-01", amount: 145.60, method: "银行代扣", type: "月度账单", balance: 184.16, icon: "ri-bank-line", iconColor: "text-gray-500" },
  { date: "2025-11-15", amount: 100, method: "支付宝", type: "充值", balance: 329.76, icon: "ri-alipay-line", iconColor: "text-sky-500" },
];

const monthlySpend = [
  { month: "10月", spend: 98.40, recharge: 100 },
  { month: "11月", spend: 98.40, recharge: 100 },
  { month: "12月", spend: 145.60, recharge: 200 },
  { month: "1月", spend: 118.90, recharge: 100 },
  { month: "2月", spend: 134.20, recharge: 0 },
  { month: "3月", spend: 102.56, recharge: 200 },
];

const payMethodStats = [
  { method: "银行代扣", count: 5, amount: 599.66, pct: 52, color: "#6b7280" },
  { method: "微信支付", count: 4, amount: 500, pct: 36, color: "#22c55e" },
  { method: "支付宝", count: 2, amount: 234.20, pct: 12, color: "#0ea5e9" },
];

export default function PurchaseAnalysis() {
  const [subTab, setSubTab] = useState<"records" | "stats" | "predict">("records");
  const chartRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (subTab !== "stats") return;
    const canvas = chartRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const maxVal = Math.max(...monthlySpend.map(d => Math.max(d.spend, d.recharge))) + 30;
    const barW = (W - 24) / monthlySpend.length;
    monthlySpend.forEach((d, i) => {
      const x = 12 + i * barW;
      const bw = barW * 0.35;
      // Spend bar
      const spendH = ((d.spend / maxVal) * (H - 28));
      const spendGrad = ctx.createLinearGradient(x, H - 20 - spendH, x, H - 20);
      spendGrad.addColorStop(0, "#E8531A");
      spendGrad.addColorStop(1, "rgba(232,83,26,0.2)");
      ctx.fillStyle = spendGrad;
      ctx.beginPath();
      ctx.roundRect(x + barW * 0.05, H - 20 - spendH, bw, spendH, 2);
      ctx.fill();
      // Recharge bar
      if (d.recharge > 0) {
        const rechargeH = ((d.recharge / maxVal) * (H - 28));
        const rechargeGrad = ctx.createLinearGradient(x + bw + barW * 0.1, H - 20 - rechargeH, x + bw + barW * 0.1, H - 20);
        rechargeGrad.addColorStop(0, "#22c55e");
        rechargeGrad.addColorStop(1, "rgba(34,197,94,0.2)");
        ctx.fillStyle = rechargeGrad;
        ctx.beginPath();
        ctx.roundRect(x + bw + barW * 0.1, H - 20 - rechargeH, bw, rechargeH, 2);
        ctx.fill();
      }
      ctx.fillStyle = "#9ca3af";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(d.month, x + barW * 0.42, H - 5);
    });
  }, [subTab]);

  const totalSpend = purchaseHistory.reduce((s, r) => s + r.amount, 0);
  const totalRecharge = purchaseHistory.filter(r => r.type === "充值").reduce((s, r) => s + r.amount, 0);
  const avgMonthly = monthlySpend.reduce((s, d) => s + d.spend, 0) / monthlySpend.length;

  return (
    <div className="p-4 space-y-4">
      <div className="text-sm font-semibold text-gray-800">购气分析</div>

      {/* Summary */}
      <div className="bg-gradient-to-br from-[#E8531A] to-[#f97316] rounded-2xl p-4 text-white">
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="text-xl font-bold">¥{totalSpend.toFixed(0)}</div>
            <div className="text-white/70 text-[10px] mt-0.5">近8月总支出</div>
          </div>
          <div className="text-center border-x border-white/20">
            <div className="text-xl font-bold">¥{totalRecharge.toFixed(0)}</div>
            <div className="text-white/70 text-[10px] mt-0.5">充值总额</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold">¥{avgMonthly.toFixed(0)}</div>
            <div className="text-white/70 text-[10px] mt-0.5">月均支出</div>
          </div>
        </div>
      </div>

      {/* Sub tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {[
          { key: "records", label: "缴费记录" },
          { key: "stats", label: "支出统计" },
          { key: "predict", label: "AI预测" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key as typeof subTab)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${subTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Records */}
      {subTab === "records" && (
        <div className="space-y-2">
          {purchaseHistory.map((rec, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
                <i className={`${rec.icon} ${rec.iconColor} text-lg`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-800">{rec.type}</span>
                  <span className={`text-sm font-bold ${rec.type === "充值" ? "text-green-600" : "text-[#E8531A]"}`}>
                    {rec.type === "充值" ? "+" : "-"}¥{rec.amount.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-[10px] text-gray-400">{rec.date} · {rec.method}</span>
                  <span className="text-[10px] text-gray-400">余额 ¥{rec.balance.toFixed(2)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {subTab === "stats" && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-700">月度支出 vs 充值</span>
              <div className="flex items-center gap-3 text-[10px] text-gray-400">
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-sm bg-[#E8531A]"></div>支出</div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-sm bg-green-500"></div>充值</div>
              </div>
            </div>
            <canvas ref={chartRef} width={320} height={110} className="w-full" />
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="text-xs font-semibold text-gray-700 mb-3">支付方式分布</div>
            <div className="space-y-2.5">
              {payMethodStats.map((p, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: p.color }}></div>
                      <span className="text-xs text-gray-700">{p.method}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-400">{p.count}次</span>
                      <span className="font-semibold text-gray-800">¥{p.amount.toFixed(0)}</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full" style={{ width: `${p.pct}%`, background: p.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI Predict */}
      {subTab === "predict" && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-[#E8531A] flex items-center justify-center">
                <i className="ri-robot-2-line text-white text-xs"></i>
              </div>
              <span className="text-xs font-semibold text-gray-800">AI购气预测</span>
              <span className="ml-auto text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full">置信度 91%</span>
            </div>
            <div className="space-y-3">
              {[
                { label: "本月预计支出", value: "¥89.34", sub: "已出账，余额充足", color: "text-[#E8531A]", icon: "ri-calendar-line" },
                { label: "下月预计支出", value: "¥95-110", sub: "气温回升，用气量将下降", color: "text-amber-600", icon: "ri-calendar-2-line" },
                { label: "建议充值时间", value: "4月25日前", sub: "余额预计降至¥30以下", color: "text-teal-600", icon: "ri-time-line" },
                { label: "建议充值金额", value: "¥100-150", sub: "可覆盖未来2个月用气", color: "text-green-600", icon: "ri-wallet-3-line" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} ${item.color} text-base`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] text-gray-400">{item.label}</div>
                    <div className={`text-sm font-bold ${item.color}`}>{item.value}</div>
                    <div className="text-[10px] text-gray-400">{item.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-orange-50 rounded-xl p-4">
            <div className="text-xs font-semibold text-[#E8531A] mb-2">AI省钱建议</div>
            <div className="space-y-2">
              {[
                "签约银行代扣可享受每月¥5话费补贴，年省¥60",
                "建议在月底前充值，避免逾期产生滞纳金",
                "您的用气量处于第一阶梯（3.45元/m³），继续保持",
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-2">
                  <i className="ri-lightbulb-line text-[#E8531A] text-xs mt-0.5 flex-shrink-0"></i>
                  <p className="text-xs text-gray-600 leading-relaxed">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
